import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

import { RootState } from "../../../redux/store";
import {
  TransactionState,
  TransactionResponse,
  TransactionFilters,
} from "./types/transaction-types";
import { ApiError } from "./types/common-types";

const initialFilters: TransactionFilters = {
  search: "",
  start_date: "",
  end_date: "",
  status: "",
  type: "",
  payment_method: "",
  page: 1,
  per_page: 15,
};

const initialState: TransactionState = {
  transactions: [],
  pagination: null,
  user: null,
  filters: { ...initialFilters },
  loading: false,
  error: null,
};

const API_URL = process.env.REACT_APP_API_URL;

export const fetchTransactions = createAsyncThunk<
  TransactionResponse,
  Partial<TransactionFilters> | void,
  { rejectValue: ApiError; state: RootState }
>("transactions/fetchAll", async (filters = {}, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) {
    return thunkAPI.rejectWithValue({ message: "Unauthorized" });
  }

  try {
    const queryParams = new URLSearchParams();
    const mergedFilters = { ...thunkAPI.getState().transactions.filters, ...filters };

    Object.entries(mergedFilters).forEach(([key, value]) => {
      if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value !== "all"
      ) {
        queryParams.append(key, value.toString());
      }
    });

    const url = `${API_URL}/my/transactions?${queryParams.toString()}`;

    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
    });

    return res.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch transactions" }
    );
  }
});

const transactionSlice = createSlice({
  name: "transactions",
  initialState,
  reducers: {
    setFilters: (
      state,
      action: PayloadAction<Partial<TransactionFilters>>
    ) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
    },
    clearTransactionsState: (state) => {
      state.transactions = [];
      state.pagination = null;
      state.user = null;
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.data.data;
        state.pagination = {
          current_page: action.payload.data.current_page,
          last_page: action.payload.data.last_page,
          per_page: action.payload.data.per_page,
          total: action.payload.data.total,
          from: action.payload.data.from,
          to: action.payload.data.to,
        };
        state.user = action.payload.user;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message || "Failed to fetch transactions";
      });
  },
});

export const { setFilters, clearFilters, clearTransactionsState } =
  transactionSlice.actions;

export default transactionSlice.reducer;
